import CloseIcon from '@mui/icons-material/Close';
import Modal from 'react-modal';
import { IHistoryViewModel, ISolicitationViewModel } from 'viewModels';
import { InputAdornment } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import ErrorIcon from '@mui/icons-material/Error';
import { yupResolver } from '@hookform/resolvers/yup';
import { IUpdateSolicitationValueInputModel } from 'inputModels';
import { useState } from 'react';
import * as yup from 'yup';
import { v4 as uuid } from 'uuid';
import { ReactComponent as IconDollar } from '../../../assets/icons/iconDollar.svg';
import { HistoryType } from '../../../types/enums';
import { Button } from '../../Buttons/Button';
import { ContentButtons, ContentModal, Header } from './styles';
import CurrencyInput from '../../CurrencyInput';
import { SolicitationController } from '../../../controller/SolicitationController';
import { useAlert } from '../../../hooks/useAlert';
import { useSolicitation } from '../../../context/solicitation';
import { useAuth } from '../../../context';

interface Props {
  isOpen: boolean;
  solicitation: ISolicitationViewModel;
  onRequestClose: () => void;
  zIndex?: number;
}
export function ModalSolicitationValue({ isOpen, solicitation, onRequestClose, zIndex }: Props) {
  const alert = useAlert();
  const { solicitations, setSolicitations } = useSolicitation();
  const { user } = useAuth();
  const [defaultValues] = useState<IUpdateSolicitationValueInputModel>();
  const registerSchema: yup.SchemaOf<IUpdateSolicitationValueInputModel> = yup.object({
    value: yup
      .number()
      .required('Informe o novo valor')
      .min(1, 'O valor deve ser maior que R$1,00')
      .test('sameValue', 'Valor deve ser diferente do cadastrado', (value?: number) => {
        try {
          return value !== solicitation.value;
        } catch {
          return false;
        }
      }),
    solicitationId: yup.string(),
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(registerSchema),
    reValidateMode: 'onChange',
    defaultValues,
  });
  const handleSendNewValue = (data: IUpdateSolicitationValueInputModel) => {
    if (!solicitation.id) return;
    const update: IUpdateSolicitationValueInputModel = {
      value: data.value,
      solicitationId: solicitation.id,
    };
    SolicitationController.updateSolicitationValue(update)
      .then(() => {
        const history: IHistoryViewModel = {
          id: uuid(),
          createdBy: `${user?.firstName} ${user?.lastName}`,
          type: HistoryType.UPDATE_SOLICITATION_VALUE,
          solicitationId: solicitation.id,
          createdAt: new Date(),
          visible: true,
          newSolicitationValue: update.value,
          prevSolicitationValue: solicitation.value,
        };
        const solicitationIdx = solicitations.findIndex((s) => s.id === solicitation.id);
        if (solicitationIdx !== -1) {
          const newSolicitations = solicitations;
          newSolicitations[solicitationIdx].value = update.value;
          newSolicitations[solicitationIdx].history?.push(history);
          newSolicitations[solicitationIdx].history?.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
          setSolicitations([...newSolicitations]);
        }
        alert.show({
          type: 'success',
          message: 'Valor atualizado com sucesso',
        });
        onRequestClose();
      })
      .catch(() => {
        alert.show({
          type: 'error',
          message: 'Erro ao atualizar valor',
        });
      });
  };
  const setZIndex =
    zIndex !== undefined
      ? {
          overlay: {
            zIndex: zIndex || 1,
          },
        }
      : {};
  return (
    <Modal
      style={setZIndex}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-add-modal-content">
      <Header>
        <div className="subHeader">
          <IconDollar />
          <h1>Novo valor</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <ContentModal>
        <Controller
          control={control}
          name="value"
          render={({ field: { onChange, value } }) => (
            <CurrencyInput
              value={value}
              variant="outlined"
              size="small"
              className="inputSuggestionValue"
              InputProps={{
                startAdornment: (
                  <InputAdornment sx={{ pl: 1 }} position="start">
                    R$
                  </InputAdornment>
                ),
              }}
              onChange={onChange}
              error={!!errors.value}
            />
          )}
        />
        {errors?.value && (
          <span className="fieldError">
            <ErrorIcon fontSize="inherit" /> {errors.value.message}
          </span>
        )}
      </ContentModal>
      <ContentButtons>
        <Button
          text="Salvar"
          style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
          dimensions={{
            width: '6rem',
            height: '2rem',
          }}
          positionLeftElement={5}
          border={{ color: 'secondary', width: 0 }}
          color="background_white"
          background="secondary"
          onClick={handleSubmit(handleSendNewValue)}
        />
      </ContentButtons>
      <ContentModal>
        <>
          <p className="title">Historico</p>
          <div className="content-map-date">
            <p className="header-table">Valor</p>
            <p className="header-table">Novo Valor</p>
            <p className="header-table">Nome</p>
            <p className="header-table">Data</p>
          </div>
          {solicitation.history
            ?.filter((h) => Number(h.type) === Number(HistoryType.UPDATE_SOLICITATION_VALUE))
            .map((item) => (
              <div className="content-map-date" key={item.id}>
                <p className="item-in-table">
                  {item.prevSolicitationValue !== undefined &&
                    new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(item.prevSolicitationValue)}
                </p>
                <p className="item-in-table">
                  {item.newSolicitationValue !== undefined &&
                    new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(item.newSolicitationValue)}
                </p>
                <p className="item-in-table">{item.createdBy}</p>
                <p className="item-in-table">
                  {new Date(item.createdAt).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            ))}
        </>
      </ContentModal>
    </Modal>
  );
}
